import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { PageLayout } from 'dibs-elements/exports/PageLayout';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { HpRespLocationsModuleItem } from './HpRespLocationsModuleItem';
import { HpRespLocationsModuleItemList } from './HpRespLocationsModuleItemList';
import {
    trackShopByLocationsImpressions,
    trackShopByLocationClick,
} from '../helpers/shopByTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { HpRespLocationItems } from './HpRespLocationItems';

import styles from './HpRespLocationsModule.scss';

const LOCATION_ITEM_PAGE_COUNT = 2;

export const handleInitialDisplay = ({ inViewport, locations }) => {
    if (locations && inViewport) {
        locations.map((location, index) => {
            const { linkData, __typename } = location;
            const isPersonalized = __typename === 'LocationPage';
            trackShopByLocationsImpressions(index, get(linkData, 'path'), isPersonalized);
        });
    }
};

export const HpRespLocationsModuleComponent = props => {
    const locationsModule = get(props, 'homepage.locationsModule') || {};
    const { items, title, subtitle, location } = locationsModule;
    const {
        additionalTopSpacing,
        numOfVisibleLocations,
        showLocationLink,
        pageSetOffset,
        viewer,
        itemsPerPage,
    } = props;
    const locations = items || [];
    const locationImages = locations.map(({ imageUrl }) => imageUrl);

    const locationPage = get(viewer, 'regionalInfo[0].locationPage');
    const locationModules = useMemo(() => {
        let locationResult = locations;
        if (locationPage) {
            locationResult = [
                locationPage,
                ...locations.filter(locObj => locObj.displayName === locationPage.displayName),
            ];
        }
        return locationResult.slice(0, numOfVisibleLocations);
    }, [locations, locationPage, numOfVisibleLocations]);

    if (!locations.length) {
        return null;
    }

    return (
        <HpRespModuleContainer>
            <PageLayout setOffset={pageSetOffset} setViewportBackground={'sassyColorWhite'}>
                <div className={styles.content}>
                    <HpRespModuleContainer.Header title={title} subtitle={subtitle} />
                    <SharedInViewport
                        stopWhenInViewport
                        onInViewportChange={({ inViewport }) =>
                            handleInitialDisplay({
                                locations: [...locationModules, location],
                                inViewport,
                            })
                        }
                    >
                        <div className={`${styles.locations} colXs12`}>
                            {locationModules.map((item, index) => {
                                return (
                                    <HpRespLocationsModuleItem
                                        key={item.cmsId || index}
                                        item={{ ...item, imageUrl: locationImages[index] }}
                                        showLocation={!!viewer}
                                        showLocationLink={showLocationLink}
                                        onClick={() => trackShopByLocationClick(item, index)}
                                    />
                                );
                            })}
                            <HpRespLocationsModuleItemList
                                location={location}
                                ipLink={get(locationPage, 'linkData.path')}
                                additionalTopSpacing={additionalTopSpacing}
                            />
                        </div>
                        <HpRespLocationItems
                            viewer={viewer}
                            itemCount={itemsPerPage * LOCATION_ITEM_PAGE_COUNT}
                        />
                    </SharedInViewport>
                </div>
            </PageLayout>
        </HpRespModuleContainer>
    );
};

HpRespLocationsModuleComponent.propTypes = {
    homepage: PropTypes.object,
    viewer: PropTypes.object,
    numOfVisibleLocations: PropTypes.number,
    pageSetOffset: PropTypes.bool,
    showLocationLink: PropTypes.bool,
    additionalTopSpacing: PropTypes.bool,
    truncateSubTitle: PropTypes.bool,
    itemsPerPage: PropTypes.number,
};

export const HpRespLocationsModule = createFragmentContainer(HpRespLocationsModuleComponent, {
    viewer: graphql`
        fragment HpRespLocationsModule_viewer on Viewer {
            regionalInfo(
                regionTypes: "ITEM_LOCATION"
                zipCode: $zipCode
                countryCode: $userCountryCode
            ) @include(if: $isClient) {
                locationPage {
                    __typename
                    title: displayName # Alias to match the title field on cms items field
                    linkData {
                        # Used by HpRespLocationsModuleItem
                        path
                        ...SeoLink_linkData
                    }
                }
            }
            ...HpRespLocationItems_viewer
        }
    `,
    homepage: graphql`
        fragment HpRespLocationsModule_homepage on Homepage {
            locationsModule: module(type: "locations") {
                ... on LocationsModule {
                    title
                    subtitle
                    location {
                        title
                        imageUrl
                        items {
                            cmsId
                            title
                            linkData {
                                # Used by HpRespLocationsModuleItemList
                                path
                                ...SeoLink_linkData
                            }
                        }
                    }
                    items {
                        cmsId
                        title
                        imageUrl
                        linkData {
                            # Used by HpRespLocationsModuleItem
                            path
                            ...SeoLink_linkData
                        }
                    }
                }
            }
        }
    `,
});
